import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AppState } from '../store/state';
import {
  selectAssignmentState,
  selectConference,
  selectCurrentRole,
  selectEmailState,
  selectSubmissionState,
  selectPaperStatusState,
} from '../store/selectors';
import { Role, ConferenceState } from '../store/conference/types';
import { getRoleName } from '../helpers/translations';
import { getSupervisorRoleIds } from '../helpers/form';
import { useTranslation } from 'react-i18next';
import { EmailRecipientType, EmailSendDTO, EmailTemplate, PlaceholderByRecipientType } from '../store/email/types';
import EmailForm from '../components/EmailForm/EmailForm';
import { Review } from '../store/review/types';
import { CategoryEnum } from '../store/paper-status/types.d';
import styled from 'styled-components';
import { apiCall } from '../store/api';
import { baseURL } from '../store/root-saga';

type SendState = 'compose' | 'sending' | 'done' | 'error';

interface Props {
  currentRole: Role | null;
  conferenceState: ConferenceState;
  assignments: Review[];
  placeholdersByRecipientType: PlaceholderByRecipientType;
  templatesById: { [key: number]: EmailTemplate };
  submissionsById: { [key: string]: Submission };
  paperStatusById: { [key: number]: PaperStatus };
}

const BulkEmailSupervisorsPage: React.FC<Props> = ({
  currentRole,
  conferenceState,
  assignments,
  placeholdersByRecipientType,
  templatesById,
  submissionsById,
  paperStatusById,
}) => {
  const { t } = useTranslation();

  const supervisorRoleIds = currentRole ? getSupervisorRoleIds(conferenceState.roleById, currentRole) : [];
  const emailRoleIds = conferenceState.roleRelationsById['send-email']?.[currentRole?.id ?? 0] ?? [];
  const inProgressSubmissionIds = new Set(
    Object.values(submissionsById)
      .filter((s) => {
        const ps = paperStatusById[s.paper_status_id];
        return ps && ps.category === CategoryEnum.In_Progress;
      })
      .map((s) => s.id),
  );

  const mySubmissionIds = new Set(
    currentRole
      ? assignments
          .filter((a) => a.role_id === currentRole.id && inProgressSubmissionIds.has(a.submission_id))
          .map((a) => a.submission_id)
      : [],
  );

  const supervisorRoles = supervisorRoleIds
    .filter((id) => emailRoleIds.includes(id))
    .map((id) => conferenceState.roleById[id])
    .filter((r): r is Role => !!r)
    .filter((role) => assignments.some((a) => a.role_id === role.id && mySubmissionIds.has(a.submission_id)));

  const recipientType: EmailRecipientType = 'submission:ids';

  const getSubmissionGroups = (): { submissionId: number; recipientIds: number[] }[] => {
    const bySubmission: { [submissionId: number]: Set<number> } = {};
    supervisorRoles.forEach((role) => {
      assignments
        .filter((a) => a.role_id === role.id && mySubmissionIds.has(a.submission_id))
        .forEach((a) => {
          if (!bySubmission[a.submission_id]) {
            bySubmission[a.submission_id] = new Set();
          }
          bySubmission[a.submission_id].add(a.person_role_id);
        });
    });
    return Object.entries(bySubmission).map(([submissionId, personRoleIds]) => ({
      submissionId: parseInt(submissionId),
      recipientIds: Array.from(personRoleIds),
    }));
  };

  const [email, setEmail] = useState<EmailSendDTO>({
    ids: [],
    subject: '',
    body: '',
    dry_run: false,
    recipient_type: recipientType,
  });

  const [sendState, setSendState] = useState<SendState>('compose');
  const [progress, setProgress] = useState({ sent: 0, total: 0, errors: 0 });

  const submissionGroups = getSubmissionGroups();
  const isValid = !!email.subject && !!email.body && submissionGroups.length > 0;

  const handleSend = async () => {
    const groups = getSubmissionGroups();
    const group = groups[0];
    if (!group) return;

    setSendState('sending');
    setProgress({ sent: 0, total: 1, errors: 0 });

    const payload: EmailSendDTO = {
      ids: [group.submissionId],
      subject: email.subject,
      body: email.body,
      dry_run: false,
      recipient_type: 'submission:ids',
      recipient_ids: group.recipientIds,
      template_id: email.template_id,
    };

    let errors = 0;
    try {
      const result = await apiCall(`${baseURL}/api/emails`, {
        method: 'POST',
        auth: true,
        role: true,
        body: JSON.stringify(payload),
      });
      if (result.type !== 'ok') {
        errors++;
      }
    } catch {
      errors++;
    }

    setProgress({ sent: 1, total: 1, errors });
    setSendState(errors > 0 ? 'error' : 'done');
  };

  const handleReset = () => {
    setSendState('compose');
    setProgress({ sent: 0, total: 0, errors: 0 });
  };

  if (!currentRole || supervisorRoles.length === 0) {
    return (
      <div className="p-6">
        <p>{t('No supervising roles to email.')}</p>
      </div>
    );
  }

  return (
    <StyledWrapper className="flex flex-col h-full pb-6">
      <div className="p-6">
        <h2 className="text-xl font-bold mb-6">
          {supervisorRoles.length === 1
            ? t('Email {{role}}', { role: getRoleName(supervisorRoles[0], t) })
            : t('Email supervisors')}
        </h2>

        {sendState === 'compose' && (
          <>
            <EmailForm
              onChange={(value) => setEmail((prev) => ({ ...prev, ...value }))}
              placeholders={placeholdersByRecipientType[recipientType] ?? []}
              value={email}
              templatesById={templatesById}
              hideTemplates={currentRole?.type != 'chair'}
            />

            <div className="mt-4">
              <button
                className="bg-blue-600 text-white px-6 py-2 rounded disabled:opacity-50"
                disabled={!isValid || sendState !== 'compose'}
                onClick={handleSend}
              >
                {t('Send')}
              </button>
            </div>
          </>
        )}

        {sendState === 'sending' && (
          <div>
            <p className="mb-2 font-medium">{t('Sending emails...')}</p>
            <div className="w-full bg-gray-200 rounded h-4 mb-2">
              <div
                className="bg-blue-600 h-4 rounded transition-all"
                style={{ width: `${progress.total > 0 ? (progress.sent / progress.total) * 100 : 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-600">
              {progress.sent} / {progress.total} {t('submissions')}
              {progress.errors > 0 && (
                <span className="text-red-500 ml-2">
                  ({progress.errors} {t('errors')})
                </span>
              )}
            </p>
          </div>
        )}

        {(sendState === 'done' || sendState === 'error') && (
          <div>
            <p className={`mb-4 font-medium ${sendState === 'done' ? 'text-green-600' : 'text-orange-500'}`}>
              {sendState === 'done'
                ? t('All emails sent successfully.')
                : t('Sending completed with {{errors}} errors.', { errors: progress.errors })}
            </p>
            <button className="bg-blue-600 text-white px-6 py-2 rounded" onClick={handleReset}>
              {t('Send more')}
            </button>
          </div>
        )}
      </div>
    </StyledWrapper>
  );
};

const mapStateToProps = (state: AppState) => ({
  currentRole: selectCurrentRole(state),
  conferenceState: selectConference(state),
  assignments: selectAssignmentState(state).assignments,
  placeholdersByRecipientType: selectEmailState(state).placeholdersByRecipientType,
  templatesById: selectEmailState(state).templatesById,
  submissionsById: selectSubmissionState(state).submissionsById,
  paperStatusById: selectPaperStatusState(state).paperStatusById,
});

export default connect(mapStateToProps)(BulkEmailSupervisorsPage);

const StyledWrapper = styled.div`
  overflow-y: auto;
`;
